<template>
  <input
    ref="inputRef"
    class="base-input number-input"
    type="number"
    :data-test-id="`${props.testId}__BaseInputNumber`"
    :value="internalValue"
    :step="step"
    :disabled="disabled"
    @input="inputHandler"
    @blur="blurHandler"
  >
</template>

<script lang="ts" setup>
  import { useDebounceFn, useEventListener } from '@vueuse/core'
  import round from 'lodash/round'
  import type { PropType } from 'vue'
  import { onMounted, ref, toRef, watch } from 'vue'

  import { clamp } from '@/utils/common'

  const props = defineProps({
    debounceAfter: {
      default: 500,
      type: Number,
    },
    disabled: {
      default: false,
      type: Boolean,
    },
    getRef: {
      type: Function as PropType<(ref: HTMLInputElement) => void>,
    },
    max: {
      default: 100,
      type: Number,
    },
    min: {
      default: 0,
      type: Number,
    },
    modelValue: {
      required: true,
      type: Number,
    },
    precision: {
      default: 0,
      type: Number,
    },
    step: {
      default: 1,
      type: Number,
    },
    testId: {
      required: true,
      type: String,
    },
  })
  const clampValue = (value: number) => {
    return clamp(round(value, props.precision), props.min, props.max)
  }

  const internalValue = toRef(clampValue(props.modelValue))
  const lastValue = ref(0)
  const inputRef = ref<HTMLInputElement>()

  const emit = defineEmits(['update:modelValue', 'inputStatus'])

  const updateValue = useDebounceFn((event: Event) => {
    const target = event.target as HTMLInputElement
    lastValue.value = internalValue.value
    if (target.value !== '') {
      const value = Number(target.value)
      const clamped = clampValue(value)
      internalValue.value = value
      internalValue.value = clamped

      emit('update:modelValue', clamped)
    }

    emit('inputStatus', 'finished')
  }, props.debounceAfter)

  const inputHandler = (event: Event) => {
    emit('inputStatus', 'inprogress')
    void updateValue(event)
  }
  const blurHandler = (event: Event) => {
    const value = (event.target as HTMLInputElement).value
    if (value === '') {
      // @ts-expect-error
      internalValue.value = value
      internalValue.value = lastValue.value
    }
  }

  const handleScroll = () => {
    if (inputRef.value) {
      inputRef.value.blur()
    }
  }

  onMounted(() => {
    if (props.getRef && inputRef.value) {
      props.getRef(inputRef.value)
    }
  })

  watch(
    () => props.modelValue,
    value => {
      internalValue.value = value
    },
  )

  useEventListener(inputRef, 'wheel', handleScroll)
</script>

<style scoped>
  input::-webkit-outer-spin-button,
  input::-webkit-inner-spin-button {
    margin: 0;
    appearance: none;
  }

  input[type="number"] {
    appearance: textfield;
  }
</style>
